import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import axios from "axios";
import { FiArrowUp, FiPlus, FiRefreshCw } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { ModalShell } from "@/components/ui/ModalShell";
import { AppButton, TextArea } from "@/components/ui/primitives";
import type { ChatDomain, ChatResponse } from "@/domains/chat/types";
import { useAskDomainChat } from "@/hooks/useDomainChat";
import { formatCurrency } from "@/utils/formatters";

interface DomainChatModalProps {
  domain: ChatDomain;
  isOpen: boolean;
  onClose: () => void;
}

interface ChatTurn {
  id: number;
  question: string;
  response?: ChatResponse;
  errorKey?: string;
}

const MAX_MESSAGE_LENGTH = 2_000;

const JerryAvatar = ({ domain }: { domain: ChatDomain }) => (
  <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--ds-on-card)]" aria-hidden="true">
    <img
      src={domain === "installments" ? "/brand/jerry-j-mark-ink.svg" : "/brand/jerry-face-lilac.svg"}
      alt=""
      className="h-6 w-6 object-contain"
    />
  </span>
);

const eyebrowClass =
  "text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--app-muted-text)]";

export const DomainChatModal = ({ domain, isOpen, onClose }: DomainChatModalProps) => {
  const { t } = useTranslation("installments");
  const [message, setMessage] = useState("");
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [pendingTurnId, setPendingTurnId] = useState<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const pendingRef = useRef(false);
  const nextTurnIdRef = useRef(0);
  const askMutation = useAskDomainChat();

  useEffect(() => {
    const thread = threadRef.current;
    if (!thread) return;
    thread.scrollTo({
      top: thread.scrollHeight,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    });
  }, [turns, pendingTurnId]);

  const normalizedMessage = message.trim();
  const canSubmit = normalizedMessage.length > 0 && pendingTurnId === null;

  const getErrorKey = (error: unknown) => {
    if (!axios.isAxiosError(error)) return "chat.errorUnknown";
    if (!error.response) return "chat.errorNetwork";

    switch (error.response.status) {
      case 422:
        return "chat.errorValidation";
      case 502:
        return "chat.errorProcessing";
      case 503:
        return "chat.errorUnavailable";
      default:
        return "chat.errorUnknown";
    }
  };

  const ask = async (question = normalizedMessage, retryTurnId?: number) => {
    const normalizedQuestion = question.trim();
    if (!normalizedQuestion || pendingRef.current) return;

    pendingRef.current = true;
    const turnId = retryTurnId ?? nextTurnIdRef.current++;
    setPendingTurnId(turnId);
    setMessage("");
    setTurns((currentTurns) => {
      if (retryTurnId === undefined) {
        return [...currentTurns, { id: turnId, question: normalizedQuestion }];
      }
      return currentTurns.map((turn) =>
        turn.id === retryTurnId ? { ...turn, response: undefined, errorKey: undefined } : turn,
      );
    });

    try {
      const response = await askMutation.mutateAsync({ domain, message: normalizedQuestion });
      setTurns((currentTurns) =>
        currentTurns.map((turn) =>
          turn.id === turnId ? { ...turn, response } : turn,
        ),
      );
    } catch (error) {
      const errorKey = getErrorKey(error);
      setTurns((currentTurns) =>
        currentTurns.map((turn) =>
          turn.id === turnId ? { ...turn, errorKey } : turn,
        ),
      );
    } finally {
      pendingRef.current = false;
      setPendingTurnId(null);
    }
  };

  const startNewConversation = () => {
    if (pendingRef.current) return;
    setMessage("");
    setTurns([]);
    nextTurnIdRef.current = 0;
    textareaRef.current?.focus();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void ask();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void ask();
    }
  };

  const jumpToTurn = (turnId: number) => {
    document.getElementById(`chat-turn-${domain}-${turnId}`)?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: "center",
    });
  };

  const domainName = t(`chat.domainNames.${domain}`);

  const footer = (
    <form onSubmit={handleSubmit}>
      <label htmlFor={`${domain}-chat-message`} className="sr-only">
        {t("chat.inputLabel", { domain: domainName })}
      </label>
      <div className="relative">
        <TextArea
          ref={textareaRef}
          id={`${domain}-chat-message`}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          onKeyDown={handleKeyDown}
          maxLength={MAX_MESSAGE_LENGTH}
          rows={1}
          placeholder={t("chat.inputPlaceholder")}
          style={{ resize: "none" }}
          className="min-h-14 rounded-full bg-[var(--ds-surface-raised)] px-5 py-4 pr-16 text-sm leading-5"
        />
        <button
          type="submit"
          disabled={!canSubmit}
          className="app-focus-ring absolute right-1 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-[var(--ds-card-sun)] text-[var(--ds-on-card)] transition-[filter] hover:brightness-95 disabled:cursor-not-allowed disabled:bg-[var(--ds-line)] disabled:text-[var(--app-muted-text)]"
          aria-label={t("chat.send")}
        >
          <FiArrowUp className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
      <div className="mt-2 flex items-center justify-between gap-3 text-[11px] text-[var(--app-muted-text)]">
        <span>{t("chat.keyboardHint")}</span>
        {message.length > MAX_MESSAGE_LENGTH * 0.8 ? (
          <span className="tabular-nums">
            {message.length}/{MAX_MESSAGE_LENGTH}
          </span>
        ) : null}
      </div>
    </form>
  );

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={t("chat.title", { domain: domainName })}
      subtitle={t("chat.subtitle", { domain: domainName })}
      labelledBy={`${domain}-chat-title`}
      maxWidthClassName="max-w-[1120px]"
      panelClassName="md:h-[min(820px,90vh)]"
      mobileSheet
      footer={footer}
      contentClassName="pt-4 sm:pt-6"
    >
      <div className="grid min-h-full gap-4 md:grid-cols-[260px_minmax(0,1fr)] md:gap-6">
        <aside className="hidden min-h-0 flex-col border-r border-[var(--ds-line)] pr-5 md:flex">
          <div className="mb-4">
            <h3 className={`mb-3 ${eyebrowClass}`}>{t("chat.historyTitle")}</h3>
            <button
              type="button"
              disabled={pendingTurnId !== null}
              onClick={startNewConversation}
              className="app-focus-ring inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-[var(--ds-card-sun)] px-4 text-sm font-medium text-[var(--ds-on-card)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FiPlus className="h-4 w-4" aria-hidden="true" />
              {t("chat.newConversation")}
            </button>
          </div>
          <p className={`mb-3 ${eyebrowClass}`}>{t("chat.today")}</p>
          <nav aria-label={t("chat.historyTitle")} className="min-h-0 flex-1 space-y-2 overflow-y-auto">
            {turns.map((turn) => (
              <button
                key={turn.id}
                type="button"
                onClick={() => jumpToTurn(turn.id)}
                className="app-focus-ring flex min-h-14 w-full items-center rounded-[var(--ds-radius-lg)] bg-[var(--ds-surface-raised)] px-4 py-3 text-left text-sm leading-5 text-[var(--ds-ink)] transition-colors hover:bg-[var(--ds-surface)]"
              >
                <span className="line-clamp-2">{turn.question}</span>
              </button>
            ))}
          </nav>
        </aside>

        <main className="flex min-h-0 flex-col">
          <p className="mb-3 text-xs leading-5 text-[var(--app-muted-text)]">
            {t("chat.independentQuestionsNotice")}
          </p>
          <div
            ref={threadRef}
            role="log"
            aria-live="polite"
            aria-busy={pendingTurnId !== null}
            className="min-h-[45dvh] max-h-[58dvh] flex-1 space-y-4 overflow-y-auto rounded-[var(--ds-radius-lg)] border border-[var(--ds-line)] bg-[var(--ds-surface)] p-4 md:min-h-[56dvh] md:p-6"
          >
            <p className="text-center text-xs leading-4 text-[var(--app-muted-text)]">{t("chat.today")}</p>

            {!turns.length ? (
              <div className="flex items-start gap-3">
                <JerryAvatar domain={domain} />
                <p className="max-w-[92%] rounded-[var(--ds-radius-md)] rounded-tl-sm bg-[var(--ds-card-lilac)] px-4 py-3 text-sm leading-6 text-[var(--ds-on-card)]">
                  {t("chat.intro", { domain: domainName })}
                </p>
              </div>
            ) : null}

            {turns.map((turn) => {
              const isPending = pendingTurnId === turn.id && !turn.response && !turn.errorKey;
              const retry = () => void ask(turn.question, turn.id);

              return (
                <div key={turn.id} id={`chat-turn-${domain}-${turn.id}`} className="space-y-4 scroll-m-6">
                  <div className="ml-auto max-w-[92%] rounded-[var(--ds-radius-md)] rounded-br-sm bg-[var(--ds-surface-raised)] px-4 py-3 text-sm leading-6 text-[var(--ds-ink)]">
                    {turn.question}
                  </div>

                  {isPending ? (
                    <div className="flex items-center gap-3">
                      <JerryAvatar domain={domain} />
                      <span role="status" aria-label={t("chat.loading", { domain: domainName })} className="flex items-center gap-2 rounded-[var(--ds-radius-md)] rounded-tl-sm bg-[var(--ds-card-lilac)] px-4 py-3">
                        {[0, 1, 2].map((dot) => (
                          <span
                            key={dot}
                            aria-hidden="true"
                            className="h-2 w-2 animate-pulse rounded-full bg-[var(--ds-on-card)] motion-reduce:animate-none"
                            style={{ animationDelay: `${dot * 160}ms` }}
                          />
                        ))}
                      </span>
                    </div>
                  ) : null}

                  {turn.errorKey ? (
                    <div role="alert" className="ml-11 rounded-[var(--ds-radius-lg)] bg-[var(--app-danger-panel-bg)] p-5 text-[var(--app-text)]">
                      <p className="text-sm leading-6">{t(turn.errorKey)}</p>
                      <AppButton
                        tone="secondary"
                        size="sm"
                        className="mt-4"
                        leadingIcon={<FiRefreshCw className="h-4 w-4" />}
                        onClick={retry}
                      >
                        {t("chat.retry")}
                      </AppButton>
                    </div>
                  ) : null}

                  {turn.response ? (
                    <div className="flex items-start gap-3">
                      <JerryAvatar domain={domain} />
                      <div className="min-w-0 flex-1 space-y-4">
                        <div className="max-w-[92%] rounded-[var(--ds-radius-md)] rounded-tl-sm bg-[var(--ds-card-lilac)] px-4 py-3 text-sm leading-7 text-[var(--ds-on-card)]">
                          <p className="whitespace-pre-wrap">{turn.response.answer}</p>
                          {turn.response.has_recommendations && turn.response.recommendations?.length ? (
                            <div className="mt-3 space-y-2">
                              {turn.response.recommendations.map((recommendation, recommendationIndex) => (
                                <article
                                  key={`${recommendation.name}-${recommendationIndex}`}
                                  className="flex items-start justify-between gap-4 rounded-[var(--ds-radius-sm)] bg-[var(--ds-on-card)] px-3 py-2 text-sm leading-5 text-[var(--ds-card-lilac)]"
                                >
                                  <span className="min-w-0">{recommendation.name}</span>
                                  <span className="shrink-0 font-medium tabular-nums">
                                    {formatCurrency(recommendation.amount)}
                                  </span>
                                </article>
                              ))}
                            </div>
                          ) : null}
                        </div>

                        {turn.response.summary ? (
                          <section className="max-w-[92%] rounded-[var(--ds-radius-lg)] bg-[var(--ds-surface-raised)] px-4 py-3">
                            <p className={eyebrowClass}>{t("chat.summaryLabel")}</p>
                            <p className="mt-2 text-sm leading-6 text-[var(--ds-ink)]">{turn.response.summary}</p>
                          </section>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </main>
      </div>
    </ModalShell>
  );
};
